import { ColumnDef, CellContext } from '@tanstack/react-table';
import { DataTableColumn, DataTableRow, ColumnMeta } from '../types';

/**
 * Convert DataTableColumn to TanStack ColumnDef
 *
 * This function transforms our simplified column format to TanStack's ColumnDef format.
 * TanStack requires either accessorKey for direct data access or a custom cell renderer.
 */
export function convertToTanstackColumns<T extends DataTableRow>(columns: DataTableColumn<T>[]): ColumnDef<T>[] {
  return columns.map((col): ColumnDef<T> => {
    const meta: ColumnMeta = {
      width: col.width,
      sortFieldId: col.sortFieldId,
      pinned: col.pinned
    };

    // `className` and `hideOnLevel` are read off the columnDef by DataTableRow at render
    // time, so they must be carried through the conversion (not just `meta`), otherwise
    // per-row/per-level column styling and level-based hiding are silently dropped.
    const passthrough = {
      className: col.className,
      hideOnLevel: col.hideOnLevel,
      enableHiding: col.enableHiding,
      meta
    };

    // If column has accessorKey, create accessor column
    if (col.accessorKey) {
      return {
        id: col.id,
        header: col.header,
        accessorKey: col.accessorKey as string,
        cell: col.cell ? (context: CellContext<T, unknown>) => col.cell?.(context.row.original) : undefined,
        enableSorting: col.enableSorting,
        ...passthrough
      } as ColumnDef<T>;
    }

    // If column has custom cell renderer without accessorKey, create display column
    if (col.cell) {
      return {
        id: col.id,
        header: col.header,
        cell: (context: CellContext<T, unknown>) => col.cell?.(context.row.original),
        enableSorting: col.enableSorting ?? false,
        ...passthrough
      } as ColumnDef<T>;
    }

    // Fallback: display column without data
    return {
      id: col.id,
      header: col.header,
      cell: () => null,
      enableSorting: false,
      ...passthrough
    } as ColumnDef<T>;
  });
}
